import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { API_URL } from '../config';

// ==========================================
// META STATICE PER PAGINĂ (sursa de adevăr pentru SEO)
// Se setează SINCRON (fără API) → ajung direct în HTML-ul pre-randat (react-snap).
// Google citește HTML-ul inițial: title/descriere/canonical NU trebuie schimbate după încărcare.
// URL-urile canonice au "/" final - exact varianta servită de nginx (fără redirect).
// ==========================================
export const SITE_URL = 'https://cf-extras.ro';
export const ROUTE_META = {
  '/': {
    title: 'Extras Carte Funciară Online | Extras CF pe Email în 5 Minute',
    description: 'Extras CF online: extrasul de carte funciară vine pe email în 5 minute, PDF semnat electronic. Fără cont, plată cu cardul, acceptat la notar și bancă.',
    canonical: `${SITE_URL}/`
  },
  '/termeni-conditii': {
    title: 'Termeni și Condiții | CF-Extras.ro',
    description: 'Termenii și condițiile de utilizare a platformei CF-Extras.ro pentru comanda online a extraselor de carte funciară și plan cadastral.',
    canonical: `${SITE_URL}/termeni-conditii/`
  },
  '/politica-confidentialitate': {
    title: 'Politica de Confidențialitate | CF-Extras.ro',
    description: 'Cum colectăm, folosim și protejăm datele personale pe CF-Extras.ro, conform GDPR: date colectate, procesatori, drepturile tale și contact.',
    canonical: `${SITE_URL}/politica-confidentialitate/`
  }
};

// Setează title + description + canonical + Open Graph pentru o pagină
export const applyPageMeta = ({ title, description, canonical }) => {
  if (title) document.title = title;
  const setMeta = (attr, name, content) => {
    if (!content) return;
    let el = document.querySelector(`meta[${attr}="${name}"]`);
    if (!el) {
      el = document.createElement('meta');
      el.setAttribute(attr, name);
      document.head.appendChild(el);
    }
    el.setAttribute('content', content);
  };
  setMeta('name', 'description', description);
  setMeta('property', 'og:title', title);
  setMeta('property', 'og:description', description);
  setMeta('property', 'og:url', canonical);
  setMeta('name', 'twitter:title', title);
  setMeta('name', 'twitter:description', description);
  if (canonical) {
    let link = document.querySelector('link[rel="canonical"]');
    if (!link) {
      link = document.createElement('link');
      link.setAttribute('rel', 'canonical');
      document.head.appendChild(link);
    }
    link.setAttribute('href', canonical);
  }
};

const SEOMetaTags = () => {
  const [seo, setSeo] = useState(null);
  const location = useLocation();

  // Skip SEO injection pe paginile admin
  const isAdminPage = location.pathname.startsWith('/admin');

  // Paginile de oraș + pagina hub își setează singure title, description, canonical
  const isLocationPage = location.pathname.startsWith('/extras-carte-funciara');

  // Meta statice per pagină - SINCRON, fără API (ajung în HTML-ul pre-randat)
  useEffect(() => {
    if (isAdminPage || isLocationPage) return;
    const path = location.pathname.replace(/\/+$/, '') || '/';
    const meta = ROUTE_META[path];
    if (meta) applyPageMeta(meta);
  }, [location.pathname, isAdminPage, isLocationPage]);

  // Fetch SEO settings din API
  useEffect(() => {
    if (isAdminPage) return; // NU fetch SEO pe admin pages

    const fetchSEO = async () => {
      try {
        const response = await fetch(`${API_URL}/api/orders/seo`);
        const data = await response.json();
        setSeo(data);
      } catch (error) {
        console.error('Error fetching SEO settings:', error);
      }
    };

    fetchSEO();
  }, [isAdminPage]);

  // Injectare meta tags când SEO data e disponibilă
  useEffect(() => {
    if (!seo || isAdminPage) return; // Skip dacă e admin page
    if (!seo.enabled) return; // Skip dacă SEO e dezactivat din Admin

    // IMPORTANT (8 Oct 2026): title / description / canonical NU se mai suprascriu din Admin (API).
    // Suprascrierea după încărcare crea semnale contradictorii față de HTML-ul pre-randat.
    // Se editează în ROUTE_META (sus) + public/index.html, apoi rebuild.

    // 3. Meta keywords (pe toate paginile)
    updateOrCreateMetaTag('name', 'keywords', seo.keywords);

    // 5. Google Search Console verification (pe toate paginile)
    if (seo.googleSiteVerification) {
      updateOrCreateMetaTag('name', 'google-site-verification', seo.googleSiteVerification);
    }

    // 6. AI Description (custom meta tag pentru AI crawlers)
    if (seo.ai?.indexingEnabled && seo.ai?.description) {
      updateOrCreateMetaTag('name', 'ai-description', seo.ai.description);
    } else {
      // Remove AI description meta tag dacă indexing e disabled
      removeMetaTag('name', 'ai-description');
    }

    // 7. Robots meta tag (permite/blochează AI crawlers)
    if (seo.ai?.indexingEnabled !== undefined) {
      const robotsContent = seo.ai.indexingEnabled
        ? 'index, follow'
        : 'index, follow, noai, noimageai'; // noai = block AI crawlers (GPTBot, ChatGPT-User, etc.)
      updateOrCreateMetaTag('name', 'robots', robotsContent);
    }

  }, [seo, isAdminPage, isLocationPage]);

  // Helper: Update sau creează meta tag
  const updateOrCreateMetaTag = (attributeName, attributeValue, content) => {
    if (!content) return; // Skip dacă content e gol

    let element = document.querySelector(`meta[${attributeName}="${attributeValue}"]`);

    if (!element) {
      element = document.createElement('meta');
      element.setAttribute(attributeName, attributeValue);
      document.head.appendChild(element);
    }

    element.setAttribute('content', content);
  };

  // Helper: Remove meta tag
  const removeMetaTag = (attributeName, attributeValue) => {
    const element = document.querySelector(`meta[${attributeName}="${attributeValue}"]`);
    if (element) {
      element.remove();
    }
  };

  // Componenta nu renderează nimic (doar side effects)
  return null;
};

export default SEOMetaTags;
